import type { FieldWhere, WhereClause } from '../../../query-dsl';
import { nestCustomFieldWhere, type CustomFieldSearchColumn } from './custom-field-column';
import { nestWhereCondition } from './nested-column';

/**
 * Builds an Aurora Query DSL `where` clause for searching across multiple
 * columns using `iLike`. Produces a bare `or` array so any matching column
 * satisfies the filter.
 *
 * Column ids using dot notation (e.g. `'boundedContext.name'`) are translated
 * to the DSL's nested relation object (EXISTS filter) automatically.
 *
 * A column entry MAY also be a `CustomFieldSearchColumn` descriptor — a
 * SYNTHETIC custom-field column merged in at runtime (design.md D4). `text`
 * custom fields search `valueText` directly; `select`/`multiselect` custom
 * fields resolve the term to `optionId`s CLIENT-SIDE (via the descriptor's
 * `resolveOptionIds`, matching against the already-loaded option catalog)
 * and search `valueOptionId`/`valueOptionIds` — never a server-side join
 * to `option.search_text` (see `custom-field-column.ts`). A synthetic column
 * whose resolver finds no matching option contributes no clause (it is
 * omitted from the `or`, not emitted as an always-false `in: []`).
 *
 * @param value - The search string
 * @param columns - Column ids (or synthetic custom-field descriptors) to search in
 * @returns A DSL where clause, or undefined if value is empty or nothing to search
 *
 * @example
 * buildSearchWhere('tag', ['name'])
 * // { or: [{ name: { iLike: '%tag%' } }] }
 *
 * buildSearchWhere('iam', ['name', 'boundedContext.name'])
 * // { or: [
 * //   { name: { iLike: '%iam%' } },
 * //   { boundedContext: { name: { iLike: '%iam%' } } },
 * // ] }
 */
export function buildSearchWhere(
  value: string,
  columns: Array<string | CustomFieldSearchColumn>,
): WhereClause | undefined {
  const trimmed = value?.trim();
  if (!trimmed || columns.length === 0) return undefined;

  const terms = columns
    .map((col) => buildSearchTerm(col, trimmed))
    .filter((term): term is FieldWhere => term !== undefined);

  if (terms.length === 0) return undefined;

  return { or: terms };
}

function buildSearchTerm(
  col: string | CustomFieldSearchColumn,
  trimmed: string,
): FieldWhere | undefined {
  if (typeof col === 'string') return nestWhereCondition(col, { iLike: `%${trimmed}%` });

  if (col.type === 'text') {
    return nestCustomFieldWhere(col.definitionId, 'valueText', { iLike: `%${trimmed}%` });
  }

  const optionIds = col.resolveOptionIds(trimmed);
  if (optionIds.length === 0) return undefined;

  return col.type === 'select'
    ? nestCustomFieldWhere(col.definitionId, 'valueOptionId', { in: optionIds })
    : nestCustomFieldWhere(col.definitionId, 'valueOptionIds', { overlap: optionIds });
}
